import {
  faCheck,
  faClipboard,
  faCopy,
  faDownload,
  faInfoCircle,
  faQuestionCircle,
  faSpinner,
  faUniversity
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Alert, Button, Card, Grid, TextInput, Tooltip } from "@mantine/core";
import React, { useState } from "react";
import ABAApi from "../../../data/api/abaApi";
import {
  IPayEvent,
  OriginatorBankFormData
} from "../../../types/aba";

interface ABAExportSectionProps {
  /** Pay event to export */
  payEvent: IPayEvent;
  /** Callback when export is complete */
  onComplete: () => void;
}

/**
 * Streamlined ABA export workflow section
 * Handles originator bank details validation and file generation
 */
const ABAExportSection: React.FunctionComponent<ABAExportSectionProps> = ({
  payEvent,
  onComplete,
}) => {
  const [formData, setFormData] = useState<OriginatorBankFormData>({
    bsb: "",
    accountNumber: "",
    accountName: "",
    remitterName: "",
    userNumber: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [bsbInfo, setBsbInfo] = useState<any>(null);
  const [bsbValidating, setBsbValidating] = useState(false);
  const [clipboardSuccess, setClipboardSuccess] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);

  // Auto-validate BSB when 6 digits are entered
  const handleBSBChange = async (bsb: string) => {
    setFormData(prev => ({ ...prev, bsb }));
    setBsbInfo(null);

    if (bsb.length === 6) {
      setBsbValidating(true);
      try {
        const result = await ABAApi.validateBSB(bsb);
        if (result.success && result.data?.valid) {
          setBsbInfo(result.data.bsbInfo);
        }
      } catch (err) {
        console.log('BSB validation failed:', err);
      } finally {
        setBsbValidating(false);
      }
    }
  };

  const handleInputChange = (field: keyof OriginatorBankFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(null);
  };

  const isFormValid = () => {
    return formData.bsb.length === 6 &&
      formData.accountNumber.trim() !== "" &&
      formData.accountName.trim() !== "" &&
      formData.remitterName.trim() !== "";
  };

  const parseClipboardData = async () => {
    try {
      const text = await navigator.clipboard.readText();

      let data: any;

      // Try parsing as JSON first
      try {
        data = JSON.parse(text);
      } catch {
        // Try parsing as YAML if JSON fails
        try {
          // Simple YAML parsing for basic key-value pairs
          const lines = text.split('\n').filter(line => line.trim() && !line.trim().startsWith('#'));
          data = {};
          for (const line of lines) {
            const [key, ...valueParts] = line.split(':');
            if (key && valueParts.length) {
              const value = valueParts.join(':').trim().replace(/^['"]|['"]$/g, '');
              data[key.trim()] = value;
            }
          }
        } catch {
          throw new Error('Invalid format - expected JSON or YAML');
        }
      }

      // Map common field names to our form structure
      const mappedData: Partial<OriginatorBankFormData> = {};

      // BSB mapping
      if (data.bsb || data.BSB) {
        const bsbValue = (data.bsb || data.BSB).toString().replace(/[^0-9]/g, '').substring(0, 6);
        if (bsbValue.length === 6) {
          mappedData.bsb = bsbValue;
        }
      }

      // Account Number mapping
      if (data.accountNumber || data.account_number || data['account number'] || data.accountNo) {
        mappedData.accountNumber = (data.accountNumber || data.account_number || data['account number'] || data.accountNo).toString();
      }

      // Account Name mapping
      if (data.accountName || data.account_name || data['account name'] || data.name) {
        mappedData.accountName = (data.accountName || data.account_name || data['account name'] || data.name).toString();
      }

      // Remitter Name mapping
      if (data.remitterName || data.remitter_name || data['remitter name'] || data.remitter) {
        mappedData.remitterName = (data.remitterName || data.remitter_name || data['remitter name'] || data.remitter).toString();
      }

      // User Number mapping (optional)
      if (data.userNumber || data.user_number || data['user number'] || data.userId) {
        mappedData.userNumber = (data.userNumber || data.user_number || data['user number'] || data.userId).toString();
      }

      // Check if we have any valid mappings
      const hasValidData = Object.keys(mappedData).length > 0;

      if (!hasValidData) {
        throw new Error('No matching fields found in clipboard data');
      }

      // Update form with mapped data
      setFormData(prev => ({ ...prev, ...mappedData }));

      // Auto-validate BSB if provided
      if (mappedData.bsb) {
        handleBSBChange(mappedData.bsb);
      }

      // Show success feedback
      setClipboardSuccess(true);
      setTimeout(() => setClipboardSuccess(false), 2000);

      setError(null);
    } catch (err) {
      setError(`Paste failed: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };

  const copyDetailsAsYAML = async () => {
    try {
      // Generate YAML format from current form data
      const yamlContent = `# Bank Details
bsb: ${formData.bsb || ''}
accountNumber: ${formData.accountNumber || ''}
accountName: ${formData.accountName || ''}
remitterName: ${formData.remitterName || ''}
userNumber: ${formData.userNumber || ''}`;

      await navigator.clipboard.writeText(yamlContent);

      // Show success feedback
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);

      setError(null);
    } catch (err) {
      setError(`Copy failed: ${err instanceof Error ? err.message : 'Clipboard not available'}`);
    }
  };

  const handleGenerateABA = async () => {
    if (!isFormValid()) {
      setError("Please fill in all required fields");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const sanitizedName = payEvent.name ? payEvent.name.replace(/[^a-z0-9]/gi, '_').substring(0, 30) : 'export';
      const filename = `ABA-${sanitizedName}-${new Date().toISOString().split('T')[0]}.aba`
      const response = await ABAApi.generateABA(payEvent._id, {
        bsb: formData.bsb,
        accountName: formData.accountName,
        remitterName: formData.remitterName,
        accountNumber: formData.accountNumber,
        userNumber: formData.userNumber,
      }, filename);

      if (response.success && response.blob) {
        // File was generated successfully, trigger download
        const url = window.URL.createObjectURL(response.blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = response.filename || filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        // Clear form and complete
        setFormData({
          bsb: "",
          accountNumber: "",
          accountName: "",
          remitterName: "",
          userNumber: "",
        });
        setBsbInfo(null);

        // Show success and close
        setTimeout(() => {
          onComplete();
        }, 1000);

      } else {
        // Handle errors from the response (excluding "already marked as paid")
        const errors = response.errors || [];
        const relevantErrors = errors.filter(error =>
          !error.includes("already marked as paid")
        );

        if (relevantErrors.length > 0) {
          const errorMessage = relevantErrors.join('\n');
          setError(errorMessage);
        } else {
          setError(response.error || "Failed to generate ABA file");
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to generate ABA file");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      {/* Pay Event Info */}
      <Card className="mb-4">
        <div style={{ padding: "1rem" }}>
          <Grid>
            <Grid.Col span={{ base: 12, md: 8 }}>
              <h6 className="mb-1">{payEvent.description}</h6>
              <div className="text-muted">
                {payEvent.appointmentCount || 0} appointments • ${(payEvent.totalAmount || 0).toFixed(2)} {payEvent.currency || 'AUD'}
              </div>
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 4 }} className="text-end">
              <div className="text-muted small">Created</div>
              <div>{new Date(payEvent.createdAt).toLocaleDateString()}</div>
            </Grid.Col>
          </Grid>
        </div>
      </Card>

      {/* Single Form for ABA Export */}
      <Card>
        <Card.Section withBorder p="md">
          <div className="d-flex justify-content-between align-items-start">
            <div>
              <h5 className="mb-0">
                <FontAwesomeIcon icon={faUniversity} className="me-2" />
                Enter Your Bank Details to Generate ABA File
              </h5>
            </div>
            <div className="d-flex gap-2">
              <Tooltip
                label={copySuccess ? 'Copied bank details as YAML!' : 'Copy current bank details as YAML'}
                position="bottom"
                withArrow
              >
                <Button
                  color={copySuccess ? 'green' : 'gray'}
                  variant={copySuccess ? 'filled' : 'outline'}
                  size="sm"
                  onClick={copyDetailsAsYAML}
                  disabled={loading}
                >
                  <FontAwesomeIcon icon={copySuccess ? faCheck : faCopy} />
                </Button>
              </Tooltip>
              <Tooltip
                label={clipboardSuccess ? 'Bank details pasted!' : 'Paste bank details from clipboard (YAML/JSON)'}
                position="bottom"
                withArrow
              >
                <Button
                  color={clipboardSuccess ? 'green' : 'blue'}
                  variant={clipboardSuccess ? 'filled' : 'outline'}
                  size="sm"
                  onClick={parseClipboardData}
                  disabled={loading}
                >
                  <FontAwesomeIcon icon={clipboardSuccess ? faCheck : faClipboard} />
                </Button>
              </Tooltip>
              <Tooltip
                position="left"
                withArrow
                multiline
                label={
                  <div
                    style={{ textAlign: 'left', maxWidth: '300px', userSelect: 'text' }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <strong>Expected clipboard format:</strong><br />
                    <code style={{ userSelect: 'text', display: 'block', padding: '8px', backgroundColor: 'var(--color-admin-elevated)', border: '1px solid var(--color-table-border)', borderRadius: '4px', fontFamily: 'monospace' }}>
                      bsb: 123456<br />
                      accountNumber: 987654321<br />
                      accountName: Company Name Pty Ltd<br />
                      remitterName: Company Name<br />
                      userNumber: 12345
                    </code><br />
                    <small>Also supports JSON format and common field variations.<br />Use "Copy Details" to copy current form values in this format.</small>
                  </div>
                }
              >
                <FontAwesomeIcon
                  icon={faQuestionCircle}
                  className="text-muted"
                  style={{ cursor: 'pointer' }}
                />
              </Tooltip>
            </div>
          </div>
          <p className="text-muted small mb-0 mt-2">
            Fill in your bank account details below. The ABA file will be generated and downloaded immediately.
          </p>
        </Card.Section>
        <div style={{ padding: "1rem" }}>
          {/* Error Display */}
          {error && (
            <Alert color="red" className="mb-4">
              <strong>Cannot generate ABA file:</strong>
              {error.includes('\n') ? (
                <ul className="mb-0 mt-2">
                  {error.split('\n').map((err, index) => (
                    <li key={index}>{err}</li>
                  ))}
                </ul>
              ) : (
                <span> {error}</span>
              )}
            </Alert>
          )}

          <Grid>
            <Grid.Col span={{ base: 12, md: 6 }}>
              {/* BSB */}
              <div className="mb-3">
                <label className="form-label">
                  BSB <span className="text-danger">*</span>
                </label>
                <div className="input-group">
                  <TextInput
                    type="text"
                    placeholder="123-456"
                    value={formData.bsb}
                    onChange={(e) => handleBSBChange(e.target.value.replace(/[^0-9]/g, '').substring(0, 6))}
                    className={bsbInfo ? 'border-success' : ''}
                  />
                  {bsbValidating && (
                    <span className="input-group-text">
                      <FontAwesomeIcon icon={faSpinner} spin />
                    </span>
                  )}
                </div>
                {bsbInfo && (
                  <div className="text-success small mt-1">
                    <FontAwesomeIcon icon={faCheck} className="me-1" />
                    {bsbInfo.bankName} - {bsbInfo.branchName}
                  </div>
                )}
              </div>

              {/* Account Number */}
              <div className="mb-3">
                <label className="form-label">
                  Account Number <span className="text-danger">*</span>
                </label>
                <TextInput
                  type="text"
                  placeholder="123456789"
                  value={formData.accountNumber}
                  onChange={(e) => handleInputChange('accountNumber', e.target.value)}
                />
              </div>

              {/* User Number (Optional) */}
              <div className="mb-3">
                <label className="form-label">User Number</label>
                <TextInput
                  type="text"
                  placeholder="Optional"
                  value={formData.userNumber}
                  onChange={(e) => handleInputChange('userNumber', e.target.value)}
                />
                <div className="text-muted small">
                  Your bank's user identification number (if required) (Usually 5 or 6 digits)
                </div>
              </div>
            </Grid.Col>

            <Grid.Col span={{ base: 12, md: 6 }}>
              {/* Account Name */}
              <div className="mb-3">
                <label className="form-label">
                  Account Name <span className="text-danger">*</span>
                </label>
                <TextInput
                  type="text"
                  placeholder="Company Name Pty Ltd"
                  value={formData.accountName}
                  onChange={(e) => handleInputChange('accountName', e.target.value)}
                />
                <div className="text-muted small">
                  The name on your bank account
                </div>
              </div>

              {/* Remitter Name */}
              <div className="mb-3">
                <label className="form-label">
                  Remitter Name <span className="text-danger">*</span>
                </label>
                <TextInput
                  type="text"
                  placeholder="Company Name"
                  value={formData.remitterName}
                  onChange={(e) => handleInputChange('remitterName', e.target.value)}
                />
                <div className="text-muted small">
                  Name that appears on recipient bank statements
                </div>
              </div>
            </Grid.Col>
          </Grid>

          <hr />

          {/* Generate Button */}
          <div className="text-center">
            <Button
              color="green"
              variant="filled"
              size="lg"
              onClick={handleGenerateABA}
              disabled={!isFormValid() || loading}
              className="px-5 py-3"
            >
              {loading ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                  Generating & Downloading ABA File...
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faDownload} className="me-2" />
                  Generate & Download ABA File
                </>
              )}
            </Button>

            <div className="mt-3">
              <small className="text-muted">
                <FontAwesomeIcon icon={faInfoCircle} className="me-1" />
                The file will be automatically downloaded to your computer and ready to upload to your bank
              </small>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default ABAExportSection;
